import type { Market } from "./api";
import { MARKETS, type ConfigRead } from "./config";

/**
 * The Global rules section on the Strategies tab (c1-global-rules, DEC-1403 P9-RULES): the rules outside every strategy, each with
 * the value in force. Every value is C2's Config read in words. Nothing here judges a price.
 */
export const GLOBAL_RULES_COPY = {
  title: "Global rules",
  desc: "These apply to every strategy, on top of its own rules. This section is read only.",
  columns: ["Rule", "What it does", "In force now", "Where it is set"],
  edit: "Edit in Config",
  noControl: "No Config control yet",
  unread: "The global rules could not be read.",
  retry: "Retry",
  notRead: "Not read",
  everyRate: "Every currency has a stored rate",
  noRate: (currencies: string[]) => `No stored rate: ${currencies.join(", ")}`,
  rrp: { name: "RRP", rule: "No price below the RRP in force, unless an RRP exception with an end date is recorded. The commit refuses it otherwise." },
  floor: { name: "Floor", rule: "No price below a product floor, unless overridden." },
  fx: { name: "No exchange rate", rule: "A price not comparable with its RRP is refused, unless an RRP exception with an end date is recorded." },
  ending: { name: "Price ending", rule: "Rounds a proposed price, kept inside the strategy window. A strategy can override this." },
} as const;

const perMarket = (line: (market: Market) => string) => MARKETS.map((market) => `${market} ${line(market)}`).join(" · ");

export const rrpValue = (config: ConfigRead) => perMarket((m) => `${config.rrp[m].set_count} of ${config.rrp[m].product_count} products`);

export const floorValue = (config: ConfigRead) => perMarket((m) => {
  const n = config.floors.filter((f) => f.market === m && f.active && f.floor !== null).length;
  return `${n} ${n === 1 ? "floor" : "floors"}`;
});

export function fxValue(config: ConfigRead): string {
  const missing = (config.unmapped ?? []).map((u) => u.currency);
  return missing.length === 0 ? GLOBAL_RULES_COPY.everyRate : GLOBAL_RULES_COPY.noRate(missing);
}

/** Direction and cents as C2 serves them. Half a pair rounds nothing (c2-run-propose R11), so it reads as not set. */
export function endingValue(config: ConfigRead): string {
  const endings = config.price_endings;
  if (!endings) return GLOBAL_RULES_COPY.notRead;
  return perMarket((m) => {
    const direction = endings[m]?.direction.value;
    const ending = endings[m]?.ending.value;
    if (direction == null || ending == null || typeof direction === "object" || typeof ending === "object") return "not set";
    return `${direction} to .${String(ending).padStart(2, "0")}`;
  });
}
